/* Per-page SEO for the public marketing site, keyed by route. */
export const SITE_URL = 'https://sportsweeps.io';

const T = (s) => `${s} | SportSweeps`;

export const MARKETING_SEO = {
  '/': {
    title: 'SportSweeps: Run Sports Pools Without the Spreadsheet',
    description: 'Run golf, football and racing pools for your society, bar, workplace or friend group. Live scoring, mobile picks and a TV leaderboard, set up in 2 minutes.',
    image: '/og/og-home.jpg',
  },
  '/pool-types': {
    title: T('Sports Pool Types and Formats Explained'),
    description: 'Compare the four sports pool formats: golf Points Pools, Tier Pick Pools, football Last Man Standing and racing sweepstakes. See which suits your group.',
    image: '/og/og-pool-types.jpg',
  },
  '/golf-societies': {
    title: T('Golf Society Sweepstake Software'),
    description: 'Run a pool for every PGA Tournament including all Majors. Live scoring, a season leaderboard and your society branding, with no spreadsheet to maintain.',
    image: '/og/og-golf-societies.jpg',
  },
  '/sports-clubs': {
    title: T('Sweepstakes and Fundraisers for Sports Clubs'),
    description: 'GAA, rugby, football, cricket and tennis clubs run member socials and fundraisers with SportSweeps. One access code, entries on every phone, no envelopes to count.',
    image: '/og/og-sports-clubs.jpg',
  },
  '/bars-and-pubs': {
    title: T('Sports Pools for Bars and Pubs'),
    description: 'Fill your bar on tournament weekends with scan to join QR posters, live TV leaderboards on the big screen and zero admin behind the bar.',
    image: '/og/og-bars-and-pubs.jpg',
  },
  '/workplaces': {
    title: T('Office Sweepstakes and Workplace Pools'),
    description: 'Run the office sweepstake without the email chains. One link in the team channel, automatic scoring and reminders, ideal for charity fundraisers.',
    image: '/og/og-workplaces.jpg',
  },
  '/friend-groups': {
    title: T('Sweepstakes for Friend Groups'),
    description: 'Turn the group chat into a proper competition. One access code, picks on every phone and a leaderboard that settles the argument for you.',
    image: '/og/og-friend-groups.jpg',
  },
  '/masters-pools': {
    title: T('How to Run a Masters Pool'),
    description: 'Set up a Masters pool in 2 minutes with Points or Tier Pick formats, live Augusta scoring and a TV leaderboard your group watches all weekend.',
    image: '/og/og-masters-pools.jpg',
  },
  '/vs-spreadsheets': {
    title: T('SportSweeps vs the Spreadsheet'),
    description: 'See what changes when you retire the Excel file and the WhatsApp group: entries by QR code, live scoring every 60 seconds and deadlines that lock themselves.',
    image: '/og/og-vs-spreadsheets.jpg',
  },
  '/guides/run-a-sweepstake': {
    title: T('How to Run a Sweepstake: The Complete Guide'),
    description: 'A practical guide to running a sweepstake: choosing a format, agreeing the rules, collecting entries, scoring it fairly and keeping it alive all weekend.',
    image: '/og/og-guide-run-a-sweepstake.jpg',
  },
  '/faq': {
    title: T('Frequently Asked Questions'),
    description: 'Answers on setup time, pool types, supported sports, live scoring, TV leaderboards, custom branding and GDPR for SportSweeps sports pools.',
    image: '/og/og-faq.jpg',
  },
};

/* Structured data so search engines can render rich results. */
export const MARKETING_JSON_LD = {
  '/faq': {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      ['Who can use SportSweeps?', 'Anyone. Golf groups, friend groups, clubs, pubs, bars, work teams. No minimum size.'],
      ['How long does setup take?', 'About 2 minutes: name your group, pick a tournament, choose a format, go live.'],
      ['Is there an app to install?', 'No, everything runs in the browser on any device.'],
      ['What pool types are available?', 'Points Pools, Tier Pick Pools, Last Man Standing and racing sweepstakes.'],
      ['Which sports are supported?', 'Golf (every PGA Tournament including all Majors), football and horse racing.'],
      ['How does live scoring work?', 'Scores sync automatically from live data every 60 seconds.'],
      ['What is the TV leaderboard?', 'A full screen live leaderboard for big screens, with more than 10 templates.'],
      ['Can I customise the branding?', 'Yes, your logo and colours appear across player pages, emails and the TV leaderboard.'],
      ['Is SportSweeps GDPR compliant?', 'Yes. Only strictly necessary cookies are on by default.'],
    ].map(([q, a]) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  },
  '/guides/run-a-sweepstake': {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: 'How to run a sweepstake',
    description: 'Every good sweepstake comes down to the same five decisions. Here is how to get each one right.',
    step: [
      ['Choose your format', 'Mixed crowd, one big day: random draw. Sports fans, one event: picks pool. A group that meets every week: Last Man Standing.'],
      ['Agree the rules up front', 'Publish the rules before anyone enters: how teams are picked, how scoring works, what happens on a tie, when entries lock.'],
      ['Collect entries without the chaos', 'One link or access code, picks on their own phone, entries locked automatically at the deadline.'],
      ['Score it fairly', 'Live scores every 60 seconds, rules applied identically for everyone.'],
      ['Keep it alive all weekend', 'Put the live leaderboard where people gather and let reminders and results emails go out automatically.'],
    ].map(([name, text], i) => ({ '@type': 'HowToStep', position: i + 1, name, text })),
  },
};
